import {
  useState,
  useEffect,
  useCallback,
  startTransition,
  useMemo,
} from "react";
import { useMediaQuery } from "@mui/material";
import {
  FORMAT_READABLE_DATE_WITH_DAY,
  FORMAT_TIME,
  getMidnightTomorrowMs,
  INPUT_FORMAT,
  formatDateTimeInput,
  MIN_DATE,
  ONE_DAY_OF_MS,
  ONE_HOUR_OF_MS,
} from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { useDispatch } from "react-redux";
import type { TimeRangeFinite } from "types/types";
import {
  setFocusRange,
  updateTrendChartBinSize,
} from "../../../providers/GlobalStateProvider";
import { clamp, isEqual } from "lodash";
import { DateRangeStyles } from "./DateRangeStyles";
import { DateRangeContent } from "./DateRangeContent";
import {
  getAllowedBinSizes,
  useEffectOnce,
  useFocusRange,
  useFormatZoned,
  useTrendChartBinSize,
} from "utils/customHooks/genericHooks";
import { FormatZonedFn } from "utils/customHooks/genericHooksTypes";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import useQueryString from "utils/customHooks/useQueryString";
import qs from "query-string";
import { useNavigate } from "react-router";
import { usePrevious } from "react-use";

const minDateMsConstant = MIN_DATE.getTime();

const TrendsPageDateRange = () => {
  const focusRange = useFocusRange();
  const dispatch = useDispatch();
  const doesFitHorizontally = useMediaQuery(`(min-width: ${702}px)`);

  // focusRange is initially null
  const [focusRangeDisplay, setFocusRangeDisplay] = useState(focusRange);
  // set the focusRangeDisplay once the focusRange becomes non-null
  useEffectOnce({
    callback: () => {
      setFocusRangeDisplay(focusRange);
    },
    shouldRun: Boolean(!focusRangeDisplay && focusRange),
    dependencies: [focusRange, focusRangeDisplay, setFocusRangeDisplay],
  });

  // make sure the focusRangeDisplay updates when the focusRange updates
  // but not vice-versa
  const prevFocusRange = usePrevious(focusRange);
  useEffect(() => {
    if (
      // if the focus range has changed
      prevFocusRange &&
      !isEqual(focusRange, prevFocusRange) &&
      // make sure the focusRangeDisplay matches it
      !isEqual(focusRangeDisplay, focusRange)
    ) {
      setFocusRangeDisplay(focusRange);
    }
  }, [focusRange, prevFocusRange, focusRangeDisplay]);

  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const { startDate, startDateReadable, endDate } = useMemo(
    () =>
      getStartAndEndDates(
        focusRangeDisplay,
        true,
        formatZoned,
        timeZoneMsOffset
      ),
    [focusRangeDisplay, formatZoned, timeZoneMsOffset]
  );

  const now = Date.now();
  const midnightTomorrowMs = getMidnightTomorrowMs(now, timeZoneMsOffset);
  const { trendChartBinSize } = useTrendChartBinSize();
  const [
    {
      general: { trendsPageMaxQueryRangeDays, trendsPageMinDateMs },
    },
  ] = useFlightConfig();

  const minDateMs = Math.max(minDateMsConstant, trendsPageMinDateMs);
  const minDate = formatZoned(new Date(minDateMs), INPUT_FORMAT);
  const maxDate = formatZoned(midnightTomorrowMs, INPUT_FORMAT);

  const handleSubmit = useCallback(
    (newFocusRange?: TimeRangeFinite | null) => {
      const nextFocusRange = newFocusRange || focusRangeDisplay;
      if (!nextFocusRange || !focusRangeDisplay) {
        return;
      }
      const lower = new Date(minDate).getTime();
      const upper = midnightTomorrowMs;
      const nextFocusRangeClamped = {
        ge: clamp(nextFocusRange.ge, lower, upper),
        lt: clamp(nextFocusRange.lt, lower, upper),
      };

      if (
        nextFocusRangeClamped &&
        !isEqual(nextFocusRangeClamped, focusRange)
      ) {
        startTransition(() => {
          dispatch(setFocusRange(nextFocusRangeClamped));
          const focusRangeDuration =
            nextFocusRangeClamped.lt - nextFocusRangeClamped.ge;
          const allowedBinSizes = getAllowedBinSizes(focusRangeDuration);
          const nextBinSize = allowedBinSizes[allowedBinSizes.length - 1];
          if (nextBinSize && nextBinSize !== trendChartBinSize) {
            dispatch(updateTrendChartBinSize(nextBinSize));
          }
        });
      }
    },
    [
      focusRangeDisplay,
      minDate,
      trendChartBinSize,
      midnightTomorrowMs,
      dispatch,
      focusRange,
    ]
  );

  const handleChangeStartDate = useCallback(
    (newDate: Date) => {
      const newStartDateMs = newDate.getTime();
      if (newStartDateMs && focusRangeDisplay) {
        const lower = new Date(minDate).getTime();
        const upper = midnightTomorrowMs;
        const ge = clamp(newStartDateMs, lower, upper);
        setFocusRangeDisplay(prev => ({
          ...prev,
          ge,
          lt: prev?.lt ?? ge,
        }));
      }
    },
    [focusRangeDisplay, minDate, midnightTomorrowMs]
  );

  const handleChangeEndDate = useCallback(
    (newDate: Date) => {
      const newEndDateMs = newDate.getTime();
      if (newEndDateMs && focusRangeDisplay) {
        const lower = new Date(minDate).getTime();
        const upper = midnightTomorrowMs;
        const lt = clamp(newEndDateMs, lower, upper);
        setFocusRangeDisplay(prev => ({
          ...prev,
          ge: prev?.ge ?? lt,
          lt,
        }));
      }
    },
    [focusRangeDisplay, minDate, midnightTomorrowMs]
  );

  // when we step forward, current lt becomes new ge
  const focusRangeDuration = useMemo(
    () => (focusRangeDisplay?.lt ?? 0) - (focusRangeDisplay?.ge ?? 0),
    [focusRangeDisplay?.lt, focusRangeDisplay?.ge]
  );
  const focusRangeOneStepForward = useMemo(() => {
    const now = Date.now();
    return {
      ge: focusRangeDisplay?.lt || now,
      lt:
        (focusRangeDisplay?.lt ?? 0) + focusRangeDuration ||
        now + focusRangeDuration,
    };
  }, [focusRangeDuration, focusRangeDisplay?.lt]);

  // when we step backward, current ge becomes new lt
  const focusRangeOneStepBackward = useMemo(
    () =>
      focusRangeDisplay
        ? {
            ge: focusRangeDisplay.ge - focusRangeDuration,
            lt: focusRangeDisplay.ge,
          }
        : focusRange,
    [focusRange, focusRangeDisplay, focusRangeDuration]
  );
  const handleClickBtnPrevious = useCallback(() => {
    setFocusRangeDisplay(focusRangeOneStepBackward);
  }, [focusRangeOneStepBackward]);
  const handleClickBtnNext = useCallback(() => {
    setFocusRangeDisplay(focusRangeOneStepForward);
  }, [focusRangeOneStepForward]);

  const cantStepForward = focusRangeOneStepForward.ge > now;
  const cantStepBackward =
    !focusRangeOneStepBackward?.lt || focusRangeOneStepBackward.lt < minDateMs;

  const handleReset = useCallback(() => {
    setFocusRangeDisplay(focusRange);
  }, [focusRange]);

  const focusRangeDays = focusRangeDuration / ONE_DAY_OF_MS;
  const isAboveMaxRangeLimit = focusRangeDays > trendsPageMaxQueryRangeDays;
  const helperText = isAboveMaxRangeLimit
    ? `Max range is ${trendsPageMaxQueryRangeDays.toFixed(0)} days`
    : focusRangeDisplay && focusRangeDisplay.ge >= focusRangeDisplay.lt
      ? "Range cannot be backwards"
      : "";
  const submitEnabled = Boolean(
    !helperText &&
      !isAboveMaxRangeLimit &&
      focusRangeDisplay &&
      !isEqual(focusRange, focusRangeDisplay) &&
      focusRangeDisplay.lt - focusRangeDisplay.ge > 0
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === "Enter" && submitEnabled) {
        handleSubmit();
      }
    },
    [submitEnabled, handleSubmit]
  );

  // if we land on a page with lt <= ge, set ge = lt - ONE_HOUR_OF_MS
  const parsedQuery = useQueryString();
  const navigate = useNavigate();
  useEffectOnce({
    callback: () => {
      if (focusRange) {
        startTransition(() => {
          const newFocusRange = {
            ge: focusRange.lt - ONE_HOUR_OF_MS,
            lt: focusRange.lt,
          };
          const stringifiedQuery = qs.stringify({
            ...parsedQuery,
            ...newFocusRange,
          });
          navigate({ search: stringifiedQuery });
          dispatch(setFocusRange(newFocusRange));
        });
      }
    },
    shouldRun: Boolean(
      focusRange && focusRange.ge >= focusRange.lt && !prevFocusRange
    ),
    dependencies: [navigate, dispatch, focusRange, parsedQuery],
  });

  return (
    <DateRangeStyles
      className={`${!doesFitHorizontally ? "compact" : ""}`}
      data-component-name={"Trends Page Date Range"}
    >
      {focusRange && (
        <DateRangeContent
          {...{
            startDate,
            endDate,
            cantStepBackward,
            handleClickBtnPrevious,
            handleClickBtnNext,
            handleChangeStartDate,
            handleChangeEndDate,
            startDateReadable,
            minDate,
            maxDate,
            cantStepForward,
            handleSubmit,
            handleReset,
            submitEnabled,
            helperText,
            handleKeyDown,
          }}
        />
      )}
    </DateRangeStyles>
  );
};

export default TrendsPageDateRange;

/**
 * This function seperates the GE, LT from the given focusRange
 *
 * @param focusRangeDisplay this object contains our ge, and lt variables
 * @param isDateTime boolean that determines if the given range contains a time (True) or is just a date (false)
 * @param shouldUseCustomTimeZone boolean that determines if we should use timezone conversion (true)
 * @param timeZoneLocaleString timezone locale string i.e. America/New_York
 */
export function getStartAndEndDates(
  focusRangeDisplay: TimeRangeFinite | null,
  isDateTime = false,
  formatZoned: FormatZonedFn,
  timezoneOffsetMs: number
) {
  const geDt =
    focusRangeDisplay && focusRangeDisplay.ge
      ? new Date(+focusRangeDisplay.ge + timezoneOffsetMs)
      : null;

  const startDate = !geDt
    ? ""
    : isDateTime
      ? formatDateTimeInput(Number(geDt), formatZoned)
      : formatZoned(geDt, INPUT_FORMAT);

  const startDateReadable = !geDt
    ? ""
    : getReadableDateForHelperText(geDt, formatZoned);

  const ltDt =
    focusRangeDisplay && focusRangeDisplay.lt
      ? new Date(+focusRangeDisplay.lt + timezoneOffsetMs)
      : null;

  const endDate = !ltDt
    ? ""
    : isDateTime
      ? formatDateTimeInput(Number(ltDt), formatZoned)
      : formatZoned(ltDt, INPUT_FORMAT);

  const endDateReadable = !ltDt
    ? ""
    : getReadableDateForHelperText(ltDt, formatZoned);

  return { startDate, startDateReadable, endDate, endDateReadable };
}

export function getReadableDateForHelperText(
  date: Date,
  formatZoned: FormatZonedFn
) {
  return `${formatZoned(date, FORMAT_READABLE_DATE_WITH_DAY)} ${formatZoned(
    date,
    FORMAT_TIME
  )}`;
}
